import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

export const NETWORK_STATUSES = ['ACTIVE', 'DISABLED'] as const
export type NetworkStatus = (typeof NETWORK_STATUSES)[number]

const centerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullish(),
  status: z.enum(NETWORK_STATUSES),
  isDefault: z.boolean(),
  coversAllNas: z.boolean(),
  nasIds: z.array(z.number()),
  vispCount: z.number(),
  branchIds: z.array(uuidSchema).default([]),
})
export type NetworkCenter = z.infer<typeof centerSchema>

const vispAccountsSchema = z.object({
  allocated: z.number(),
  available: z.number(),
  used: z.number(),
  assigned: z.number(),
  active: z.number(),
  suspended: z.number(),
  expired: z.number(),
  closed: z.number(),
})

const vispSchema = z.object({
  id: uuidSchema,
  centerId: uuidSchema,
  centerName: z.string(),
  centerStatus: z.enum(NETWORK_STATUSES),
  name: z.string(),
  description: z.string().nullish(),
  status: z.enum(NETWORK_STATUSES),
  isDefault: z.boolean(),
  coversAllNas: z.boolean(),
  nasIds: z.array(z.number()),
  subscriberCount: z.number(),
  loginPrefix: z.string().nullish(),
  quota: z.number().nullish(),
  accounts: vispAccountsSchema,
  branchIds: z.array(uuidSchema).default([]),
})
export type Visp = z.infer<typeof vispSchema>

const poolAccountSchema = z.object({ id: uuidSchema, login: z.string(), createdAt: z.string() })
export type PoolAccount = z.infer<typeof poolAccountSchema>

const poolPageSchema = z.object({
  rows: z.array(poolAccountSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})
export type PoolPage = z.infer<typeof poolPageSchema>

const poolCredentialSchema = z.object({ id: uuidSchema, login: z.string(), password: z.string() })
export type PoolCredential = z.infer<typeof poolCredentialSchema>

const refSchema = z.object({ id: uuidSchema, name: z.string() })

const routerSchema = z.object({
  nasId: z.number(),
  nasname: z.string(),
  shortname: z.string().nullish(),
  centers: z.array(refSchema),
  visps: z.array(refSchema),
})
export type NetworkRouter = z.infer<typeof routerSchema>

export function routerLabel(router: NetworkRouter): string {
  return router.shortname && router.shortname !== router.nasname
    ? `${router.shortname} (${router.nasname})`
    : router.nasname
}

export interface CenterDraft {
  name: string
  description: string
  status: NetworkStatus
  coversAllNas: boolean
}

/** VISPga biriktirish mumkin bo'lgan routerlar — Center barchasini qamrasa, hammasi */
export function routersAllowedBy(center: NetworkCenter | undefined, routers: readonly NetworkRouter[]): NetworkRouter[] {
  if (!center || center.coversAllNas) return [...routers]
  return routers.filter((r) => center.nasIds.includes(r.nasId))
}

export interface VispDraft {
  centerId: string
  name: string
  description: string
  status: NetworkStatus
  coversAllNas: boolean
  loginPrefix: string
  /** Faqat yaratishda; keyin `changeVispQuota` */
  quota?: number | null
  quotaReason?: string | null
}

export const LOGIN_PREFIX = /^[a-z][a-z0-9-]{0,23}$/

export async function fetchCenters(): Promise<NetworkCenter[]> {
  const response = await api.get<unknown>('/api/network/centers')
  return z.array(centerSchema).parse(response.data)
}

export async function saveCenter(id: string | null, draft: CenterDraft): Promise<NetworkCenter> {
  const response = id
    ? await api.put<unknown>(`/api/network/centers/${id}`, draft)
    : await api.post<unknown>('/api/network/centers', draft)
  return centerSchema.parse(response.data)
}

export async function deleteCenter(id: string): Promise<void> {
  await api.delete(`/api/network/centers/${id}`)
}

export async function setCenterRouters(id: string, nasIds: number[]): Promise<NetworkCenter> {
  const response = await api.put<unknown>(`/api/network/centers/${id}/routers`, { nasIds })
  return centerSchema.parse(response.data)
}

export async function fetchVisps(): Promise<Visp[]> {
  const response = await api.get<unknown>('/api/network/visps')
  return z.array(vispSchema).parse(response.data)
}

export async function saveVisp(id: string | null, draft: VispDraft): Promise<Visp> {
  const response = id
    ? await api.put<unknown>(`/api/network/visps/${id}`, draft)
    : await api.post<unknown>('/api/network/visps', draft)
  return vispSchema.parse(response.data)
}

export async function deleteVisp(id: string): Promise<void> {
  await api.delete(`/api/network/visps/${id}`)
}

export async function changeVispQuota(id: string, quota: number, reason: string): Promise<Visp> {
  const response = await api.put<unknown>(`/api/network/visps/${id}/quota`, { quota, reason })
  return vispSchema.parse(response.data)
}

export async function fetchPoolAccounts(
  vispId: string,
  params: { search?: string; page: number; size: number },
): Promise<PoolPage> {
  const response = await api.get<unknown>(`/api/network/visps/${vispId}/accounts`, {
    params: { ...params, search: params.search?.trim() || undefined },
  })
  return poolPageSchema.parse(response.data)
}

export async function fetchPoolCredentials(vispId: string, accountIds: string[]): Promise<PoolCredential[]> {
  const response = await api.post<unknown>(`/api/network/visps/${vispId}/accounts/credentials`, { accountIds })
  return z.array(poolCredentialSchema).parse(response.data)
}

export async function setVispRouters(id: string, nasIds: number[]): Promise<Visp> {
  const response = await api.put<unknown>(`/api/network/visps/${id}/routers`, { nasIds })
  return vispSchema.parse(response.data)
}

export async function setCenterBranches(id: string, branchIds: string[]): Promise<NetworkCenter> {
  const response = await api.put<unknown>(`/api/network/centers/${id}/branches`, { branchIds })
  return centerSchema.parse(response.data)
}

export async function setVispBranches(id: string, branchIds: string[]): Promise<Visp> {
  const response = await api.put<unknown>(`/api/network/visps/${id}/branches`, { branchIds })
  return vispSchema.parse(response.data)
}

/** VISP bitta filialga biriktirilgan bo'lsa, filiali bo'sh abonentlarini shu filialga o'tkazadi; o'tganlar soni */
export async function assignVispSubscribersBranch(id: string): Promise<number> {
  const response = await api.post<unknown>(`/api/network/visps/${id}/assign-subscribers-branch`)
  return z.number().parse(response.data)
}

export async function fetchRouters(): Promise<NetworkRouter[]> {
  const response = await api.get<unknown>('/api/network/routers')
  return z.array(routerSchema).parse(response.data)
}

const userAssignmentSchema = z.object({
  vispIds: z.array(uuidSchema),
  centerIds: z.array(uuidSchema),
})
export type UserNetworkAssignment = z.infer<typeof userAssignmentSchema>

export async function fetchUserNetwork(userId: string): Promise<UserNetworkAssignment> {
  const response = await api.get<unknown>(`/api/network/users/${userId}/assignments`)
  return userAssignmentSchema.parse(response.data)
}

export async function saveUserNetwork(
  userId: string,
  assignment: UserNetworkAssignment,
): Promise<UserNetworkAssignment> {
  const response = await api.put<unknown>(`/api/network/users/${userId}/assignments`, assignment)
  return userAssignmentSchema.parse(response.data)
}
